import { HotLiveStatusRemove } from 'src/app/recruiting/candidates/new-profile/models/candidate-hotlive-status';
import { Lookup } from 'src/app/shared/models/lookup.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { CandidateOpenTicketDto } from 'src/app/recruiting/candidates/grid/models/candidate-open-ticket';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import {
    PerDiemRecruitingCandidate,
    CandidateSubmittal,
    SubmittalStatusTotals
} from 'src/app/shared/models/perdiem-recruiting-candidates.model';
import { map } from 'rxjs/operators';
import { formatDate } from 'src/app/shared/utilities/route';
import { FollowupSummary } from 'src/app/shared/models/followup-summary.model';
import { PerDiemFollowupView } from 'src/app/shared/models/perdiem-followup-view.model';
import { RecruitingFollowUpService } from 'src/app/recruiting/recruiting.service';
import { PerDiemUploadedStandardDocument } from 'src/app/shared/models/periem-uploaded-standard-document.model';

@Injectable({
    providedIn: 'root'
})
export class PerDiemRecruitingService implements RecruitingFollowUpService {
    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) private readonly settings: Settings
    ) {}

    getPerDiemRecruitingCandidates(args: FetchArgs): Observable<PagingToken<PerDiemRecruitingCandidate[]>> {
        const url = `${this.settings.coreAPIUrl}ayanova/perdiem-recruiting/candidates`;
        return this.postPaged(url, args);
    }

    getPerDiemCandidateOpenTickets(userId: number, page: number, offset: number): Observable<CandidateOpenTicketDto[]> {
        const url = `${this.settings.coreAPIUrl}ayanova/perdiem-recruiting/${userId}/open-tickets?page=${page}&offset=${offset}`;
        return this.http.get<CandidateOpenTicketDto[]>(url);
    }

    getPerDiemStatuses(): Observable<Lookup[]> {
        const url = `${this.settings.coreAPIUrl}ayanova/perdiem-recruiting/perdiem-statuses`;
        return this.http.get<Lookup[]>(url);
    }

    updatePerDiemStatus(userId: number, newStatus: number): Observable<any> {
        const url = `${this.settings.coreAPIUrl}ayanova/perdiem-recruiting/candidates/${userId}/status`;
        return this.http.put<any>(url, { newStatus });
    }

    removeLivePerDiemStatus(userId: number, data: HotLiveStatusRemove): Observable<Object> {
        const url = `${this.settings.coreAPIUrl}ayanova/perdiem-recruiting/candidates/${userId}/live-list`;
        return this.http.post(url, data);
    }

    deleteSubmittal(data: any) {
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: data
        };
        const url = `${this.settings.coreAPIUrl}ayanova/perdiem-recruiting/submittal`;
        return this.http.delete(url, options);
    }

    getSubmittalTotals(assignedToId: number): Observable<SubmittalStatusTotals[]> {
        return this.http.get<SubmittalStatusTotals[]>(
            `${this.settings.coreAPIUrl}ayanova/perdiem-recruiting/candidates/submittal-totals?assignedToId=${assignedToId}`
        );
    }

    getLeadsTotals(assignedToId: number): Observable<number> {
        return this.http.get<number>(
            `${this.settings.coreAPIUrl}ayanova/perdiem-recruiting/candidates/leads-totals?assignedToId=${assignedToId}`
        );
    }

    getFollowupsTotals(assignedToId: number): Observable<number> {
        return this.http.get<number>(
            `${this.settings.coreAPIUrl}ayanova/perdiem-recruiting/candidates/followups-totals?assignedToId=${assignedToId}`
        );
    }

    getNewApplicationTotals(assignedToId: number): Observable<number> {
        return this.http.get<number>(
            `${this.settings.coreAPIUrl}ayanova/perdiem-recruiting/candidates/new-applications-totals?assignedToId=${assignedToId}`
        );
    }

    getCandidatesSubmittalBySubmittalJobId(submittalJobId: number): Observable<CandidateSubmittal[]> {
        const url = `${this.settings.coreAPIUrl}ayanova/perdiem-recruiting/submittal/job/${submittalJobId}`;
        return this.http.get<CandidateSubmittal[]>(url);
    }

    getCandidatesSubmittalsByFacilitySubmittalId(facilitySubmittalId: number): Observable<CandidateSubmittal[]> {
        const url = `${this.settings.coreAPIUrl}ayanova/perdiem-recruiting/submittal/facility/${facilitySubmittalId}`;
        return this.http.get<CandidateSubmittal[]>(url);
    }

    getRecruiterFollowupSummary(id: number, startDate: Date, endDate: Date): Observable<FollowupSummary[]> {
        const startDatefmtd = formatDate(startDate);
        const endDatefmtd = formatDate(endDate);
        const url = `${this.settings.coreAPIUrl}ayanova/perdiem-recruiting/${id}/${startDatefmtd}/${endDatefmtd}/followup-summary`;
        return this.http.get<FollowupSummary[]>(url);
    }

    updateRecruiterResources(followup: PerDiemFollowupView): Observable<PerDiemFollowupView> {
        const url = `${this.settings.coreAPIUrl}ayanova/perdiem-recruiting/followup`;
        return this.http.post<PerDiemFollowupView>(url, followup);
    }

    getRecruiterFollowupForCandidate(candidateId: number): Observable<PerDiemFollowupView> {
        const url = `${this.settings.coreAPIUrl}/ayanova/perdiem-recruiting/followups/${candidateId}`;
        return this.http.get<PerDiemFollowupView>(url);
    }

    markAsComplete(followup: PerDiemFollowupView): Observable<Object> {
        const url = `${this.settings.coreAPIUrl}ayanova/perdiem-recruiting/complete-followup/`;
        return this.http.put(url, followup);
    }

    getCanEditFollowUpForCandidate(candidateId: number, recruiterId: number): Observable<boolean> {
        const url = `${this.settings.coreAPIUrl}ayanova/perdiem-recruiting/can-edit-followups?candidateId=${candidateId}&recruiterId=${recruiterId}`;
        return this.http.get<boolean>(url);
    }

    getPerDiemUploadedStandardDocuments(args: FetchArgs): Observable<PagingToken<PerDiemUploadedStandardDocument[]>> {
        const url = `${this.settings.coreAPIUrl}ayanova/perdiem-recruiting/initial-docs`;
        return this.postPaged(url, args);
    }

    protected getPaged<T>(url: string): Observable<PagingToken<T[]>> {
        return this.http.get<T[]>(url, { observe: 'response' }).pipe(
            map((resp) => {
                console.log('resp: ', resp);
                const totalCount = resp.body['total'];
                return {
                    total: totalCount,
                    data: resp.body
                };
            })
        );
    }

    protected postPaged<T>(url: string, fetchArgs: FetchArgs): Observable<PagingToken<T[]>> {
        return this.http.post<PagingToken<T[]>>(url, fetchArgs);
    }
}
